import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchBlogBySlug, getImageUrl } from '../services/api';
import { FiArrowLeft, FiShare2, FiClock, FiTag, FiCalendar } from 'react-icons/fi';

const BlogDetail = () => {
  const { blogSlug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadBlog = async () => {
      try {
        setLoading(true);
        const blogData = await fetchBlogBySlug(blogSlug);
        
        if (!blogData) {
          setError('Blog post not found');
        } else {
          setBlog(blogData);
        }
      } catch (err) {
        console.error('Error loading blog:', err);
        setError('Failed to load blog post. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadBlog();
  }, [blogSlug]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const getReadingTime = (text) => {
    const wordsPerMinute = 200;
    const words = text?.split(/\s+/).length || 0;
    const minutes = Math.ceil(words / wordsPerMinute);
    return minutes;
  };

  const renderBlocks = (blocks) => {
    if (!blocks || !Array.isArray(blocks)) return null;

    return blocks.map((block, index) => {
      // Handle different block component types
      if (!block.__component) return null;

      switch (block.__component) {
        // Rich text block - supports both naming conventions
        case 'shared.rich-text':
        case 'blocks.rich-text':
          return (
            <div
              key={index}
              className="prose prose-sm sm:prose lg:prose-lg max-w-none mb-6"
              dangerouslySetInnerHTML={{ __html: block.body || block.content || '' }}
            />
          );

        // Media/Image block
        case 'shared.media':
        case 'blocks.media':
          return (
            <div key={index} className="mb-6">
              {block.media && (
                <img
                  src={getImageUrl(block.media, 'large')}
                  alt={block.caption || 'Blog media'}
                  className="w-full rounded-lg shadow-md mb-2"
                />
              )}
              {block.caption && (
                <p className="text-sm text-gray-600 text-center italic">
                  {block.caption}
                </p>
              )}
            </div>
          );

        // Quote block
        case 'shared.quote':
        case 'blocks.quote':
          return (
            <blockquote
              key={index}
              className="border-l-4 border-blue-600 pl-4 italic text-gray-700 mb-6 py-2"
            >
              <p className="mb-2">"{block.quote || ''}"</p>
              {block.author && (
                <footer className="text-sm font-semibold text-gray-600">
                  — {block.author}
                </footer>
              )}
            </blockquote>
          );

        // Image slider block
        case 'shared.slider':
        case 'blocks.slider':
          return (
            <div key={index} className="mb-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {block.slides &&
                  Array.isArray(block.slides) &&
                  block.slides.map((slide, slideIndex) => (
                    <img
                      key={slideIndex}
                      src={getImageUrl(slide.image, 'medium')}
                      alt={slide.caption || `Slide ${slideIndex + 1}`}
                      className="rounded-lg shadow-md w-full h-40 object-cover"
                    />
                  ))}
              </div>
            </div>
          );

        // File download block
        case 'shared.file':
        case 'blocks.file':
          return (
            <div key={index} className="mb-6">
              <a
                href={getImageUrl(block.file)}
                download
                className="inline-flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
              >
                <span className="mr-2">📥</span>
                Download: {block.title || 'File'}
              </a>
            </div>
          );

        default:
          return null;
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error || 'Blog post not found'}
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-semibold"
          >
            <FiArrowLeft className="mr-2" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const readingTime = getReadingTime(blog.description);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Back Button */}
      <div className="sticky top-0 z-40 mt-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Link
            to="/blog"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold hover:gap-2 transition-all gap-1 bg-white px-4 py-2 rounded-lg shadow-md hover:shadow-lg"
          >
            <FiArrowLeft size={20} />
            <span>Back to All Articles</span>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Cover Image */}
        {blog.cover && (
          <div className="mb-12 -mx-4 sm:-mx-6 lg:-mx-8 relative group">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img
                src={getImageUrl(blog.cover, 'large')}
                alt={blog.title}
                className="w-full h-96 object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
            </div>
          </div>
        )}

        {/* Header */}
        <header className="mb-12">
          {/* Category Badge */}
          {blog.category && (
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full">
                <FiTag size={16} />
                <span className="text-sm font-bold">{blog.category.name}</span>
              </div>
            </div>
          )}

          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-6 mb-6 text-base text-gray-600 border-b border-gray-200 pb-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <FiClock className="text-blue-600" size={18} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Reading Time</p>
                <p className="font-semibold text-gray-900">{readingTime} minutes</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <FiCalendar className="text-green-600" size={18} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Published</p>
                <p className="font-semibold text-gray-900">{formatDate(blog.createdAt)}</p>
              </div>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            {blog.title}
          </h1>

          {/* Description/Excerpt */}
          {blog.description && (
            <p className="text-2xl text-gray-700 leading-relaxed mb-8 italic border-l-4 border-blue-600 pl-6">
              {blog.description}
            </p>
          )}

          {/* Share Buttons */}
          <div className="flex items-center gap-4 pt-8">
            <span className="text-sm font-semibold text-gray-600 flex items-center gap-2">
              <FiShare2 size={18} className="text-blue-600" />
              Share on:
            </span>
            <div className="flex gap-3">
              <a
                href={`https://twitter.com/intent/tweet?url=${window.location.href}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-blue-400 hover:bg-blue-500 text-white rounded-full flex items-center justify-center transition-all transform hover:scale-110"
                title="Share on Twitter"
              >
                𝕏
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center transition-all transform hover:scale-110"
                title="Share on Facebook"
              >
                f
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-blue-700 hover:bg-blue-800 text-white rounded-full flex items-center justify-center transition-all transform hover:scale-110"
                title="Share on LinkedIn"
              >
                in
              </a>
            </div>
          </div>
        </header>

        {/* Blog Content */}
        <div className="prose prose-sm sm:prose lg:prose-lg max-w-none mb-16 prose-img:rounded-xl prose-img:shadow-lg prose-blockquote:border-l-4 prose-blockquote:border-blue-600 prose-blockquote:bg-blue-50 prose-blockquote:py-4 prose-blockquote:px-6">
          {blog.blocks ? (
            renderBlocks(blog.blocks)
          ) : (
            <p className="text-gray-600 text-lg">{blog.description}</p>
          )}
        </div>

        {/* Footer Section */}
        <div className="border-t-2 border-gray-300 pt-12 mt-16">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Ready to explore more?</h3>
            <p className="text-gray-700 mb-6">Discover more insightful articles and industry updates from our blog.</p>
            <Link
              to="/blog"
              className="inline-flex items-center bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transform hover:scale-105 transition-all font-semibold shadow-lg hover:shadow-xl"
            >
              <FiArrowLeft className="mr-2" size={20} />
              <span>Back to All Articles</span>
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogDetail;